import { useEffect, useState, useRef } from "react";
import { Form, Input, Col, Row, Divider, Button, InputNumber, Card, DatePicker, Select, Space, message, Collapse } from "antd";
import { API_METHODS } from "../../../constants";
import useApi from "../../../hooks/useApi";
import { CloseOutlined, DeleteOutlined, MailOutlined, MinusCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDateToDayJS, isEmpty } from "../../../generalFunctions";
import { useReactToPrint } from 'react-to-print';
import QuotationPrint from "./QuotationPrint";
import EmailModal from "../../common/EmailModal";
import html2pdf from 'html2pdf.js';
import { QuotationEmailFormat } from "./QuotationEmailFormat";

function AddQuotation() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [clients, setClients] = useState();
  const [paymentModes, setPaymentModes] = useState();
  const [shipmentModes, setShipmentModes] = useState();
  const [productAccessories, setProductAccessories] = useState();
  const [productSpecificationUniqueFieldName, setProductSpecificationUniqueFieldName] = useState();
  const [defaultTnC, setDefaultTnC] = useState();
  const [products, setProducts] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [printRecord, setPrintRecord] = useState();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const location = useLocation();
  const [response, request] = useApi("/quotation", null, API_METHODS.POST);
  const [getClientsResponse, getClientsRequest] = useApi("/config/customer/dropdown", null, API_METHODS.GET);
  const [getPaymentModesResponse, getPaymentModesRequest] = useApi("/common/payment-modes", null, API_METHODS.GET);
  const [getShipmentModesResponse, getShipmentModesRequest] = useApi("/common/shipment-modes", null, API_METHODS.GET);
  const [getProductsResponse, getProductsRequest] = useApi("/config/product/dropdown", null, API_METHODS.GET);
  const [getDefaultTnCResponse, getDefaultTnCRequest] = useApi("/quotation/default-conditions", null, API_METHODS.GET);
  const [getProductAccessoriesResponse, getProductAccessoriesRequest] = useApi();
  const [getProductSpecificationResponse, getProductSpecificationRequest] =
    useApi();

  const [getQuotationResponse, getQuotationRequest] = useApi(
    `/quotation/${location.state && location.state.id}`,
    null,
    API_METHODS.GET
  );
  const [mailQuotationResponse, mailQuotationRequest] = useApi(
    `/quotation/mail/${location.state && location.state.id}`,
    null,
    API_METHODS.POST,
    true
  );

  const componentRef = useRef();
  const dataRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: selectedRecord ? `Quotation ${selectedRecord.quotation_number}` : "Quotation",
    onAfterPrint: () => console.log('Printed PDF successfully!'),
  });

  const { TextArea } = Input;

  const [editResponse, editRequest] = useApi(
    `/quotation/${location.state && location.state.id}`,
    null,
    API_METHODS.PUT
  );

  useEffect(() => {
    if (location.state) {
      getQuotationRequest();
    }
    else {
      getDefaultTnCRequest();
    }
    getClientsRequest();
    getPaymentModesRequest();
    getShipmentModesRequest();
    getProductsRequest();
  }, []);


  const handleOk = () => {
    form.validateFields().then((values) => {
      values.shipment_date = values.shipment_date?.format("YYYY-MM-DD");

      values.terms_conditions = [];
      for (let i = 1; i < 12; i++) {
        if (!isEmpty(values[i])) {
          values.terms_conditions.push({
            id: i,
            text: values[i]
          });
        }
        delete values[i];
      }
      values.other_conditions?.forEach((item, index) => {
        item.id = values.terms_conditions?.length + index + 1;
      });
      values.other_conditions = values.other_conditions ? values.other_conditions : [];
      values.terms_conditions = [...values.terms_conditions, ...values.other_conditions];
      delete values.other_conditions;

      values.product_details.forEach(product => {
        product.specifications?.forEach((spec, index) => {
          if (!spec.specification_order) {
            spec.specification_order = index + 1;
          }
        })
        product.specifications = product.specifications ? JSON.stringify(product.specifications) : null;
      })
      if (location && location.state) {
        editRequest(values);
      } else {
        request(values, API_METHODS.POST);
      }
    })
      .catch(() => { });
  };

  const handleCancel = () => {
    form.resetFields();
    navigate("/portal/quotation");
  };

  const printQuotation = () => {
    setPrintRecord(selectedRecord, handlePrint());
  }

  const getAccessories = (productId) => {
    getProductAccessoriesRequest(
      null,
      API_METHODS.GET,
      `/config/accessory/dropdown/${productId}`
    );
  }

  const onProductSelect = (productId, fieldName) => {
    getProductAccessoriesRequest(
      null,
      API_METHODS.GET,
      `/config/accessory/dropdown/${productId}`
    );

    getProductSpecificationRequest(
      null,
      API_METHODS.GET,
      `/config/product/get-specifications/${productId}`
    );


    const product = products.find((product) => product.id == productId);
    setProductSpecificationUniqueFieldName(fieldName);
    form.setFieldValue(["product_details", fieldName, "price"], product.price);
  };

  const calculateProductDetailsTotal = (name) => {
    const productDetails = form.getFieldValue("product_details");
    if (name !== undefined) {
      const rate = productDetails[name].price;
      const unit = productDetails[name].quantity;
      const discountPercent = productDetails[name].discount_percent;
      const discountAmount = productDetails[name].discount_amount;
      if (rate && unit) {
        productDetails[name].amount = rate * unit;
        if (discountAmount) {
          productDetails[name].amount = productDetails[name].amount - discountAmount;
        }
        if (discountPercent) {
          productDetails[name].amount = productDetails[name].amount - (productDetails[name].amount * discountPercent / 100);
        }
        form.setFieldValue("product_details", productDetails);
      }
    }
    //total_item_amount
    let total_item_amount = 0;
    productDetails.forEach(element => {
      if (element?.amount) {
        total_item_amount = total_item_amount + element.amount;
        element.product_accessories?.forEach(a => {
          if (a?.amount)
            total_item_amount = total_item_amount + a.amount;
        });
      }
    });
    form.setFieldValue("total_item_amount", total_item_amount);
    form.setFieldValue("total", total_item_amount);
  }

  const calculateProductAccessoriesTotal = (name, subFieldName) => {
    const productDetails = form.getFieldValue("product_details");
    const accessoryList = productDetails[name].product_accessories;
    const accessory = accessoryList[subFieldName];
    const rate = accessory.price;
    const unit = accessory.quantity;
    if (rate && unit) {
      accessory.amount = rate * unit;
      form.setFieldValue("product_details", productDetails);
    }

    //total_item_amount
    let total_item_amount = 0;
    productDetails.forEach(element => {
      if (element?.amount) {
        total_item_amount = total_item_amount + element.amount;
        element.product_accessories?.forEach(a => {
          if (a?.amount)
            total_item_amount = total_item_amount + a.amount;
        });
      }
    });
    form.setFieldValue("total_item_amount", total_item_amount);
    form.setFieldValue("total", total_item_amount);
  }

  const calculateTotalAmount = () => {
    const { total_item_amount, total_discount_percent, total_discount_amount, shipping, bank_charges, adjustment } = form.getFieldsValue();
    let totalAmount = total_item_amount;
    if (!isEmpty(total_discount_amount))
      totalAmount = totalAmount - total_discount_amount;
    if (!isEmpty(total_discount_percent))
      totalAmount = totalAmount - (total_item_amount * total_discount_percent / 100);
    if (!isEmpty(shipping))
      totalAmount = totalAmount + shipping;
    if (!isEmpty(bank_charges))
      totalAmount = totalAmount + bank_charges;
    if (!isEmpty(adjustment))
      totalAmount = totalAmount + adjustment;

    form.setFieldValue("total", totalAmount);
  }

  const sendEmail = async () => {
    const formData = new FormData();
    const data = dataRef.current;
    for (const name in data) {
      if (name === "email-attachments") {
        if (data["email-attachments"] !== undefined) {
          for (const attachment of data["email-attachments"].fileList) {
            formData.append(
              "attachments",
              attachment.originFileObj,
              attachment.name
            );
          }
        }
      } else {
        formData.append(name, data[name]);
      }
    }
    const html = componentRef.current;
    if (html) {
      const quotationFile = await html2pdf().from(html).output("blob");
      formData.append("quotation-file", quotationFile);
    }
    mailQuotationRequest(formData);
  };


  const sendQuotationEmail = (data) => {
    dataRef.current = data;
    sendEmail();
  }

  const deleteCondition = (conditionId) => {
    let newConditions = [];
    if (selectedRecord) {
      let record = { ...selectedRecord };
      record.terms_conditions = record.terms_conditions.filter(c => c.id !== conditionId);
      setSelectedRecord(record);
    } else {
      newConditions = defaultTnC.filter(c => c.id !== conditionId);
      setDefaultTnC(newConditions);
    }
  }


  useEffect(() => {
    if (getClientsResponse?.data) {
      setClients(getClientsResponse.data);
    }
  }, [getClientsResponse]);

  useEffect(() => {
    if (getPaymentModesResponse?.data) {
      setPaymentModes(getPaymentModesResponse.data);
    }
  }, [getPaymentModesResponse]);

  useEffect(() => {
    if (getShipmentModesResponse?.data) {
      setShipmentModes(getShipmentModesResponse.data);
    }
  }, [getShipmentModesResponse]);

  useEffect(() => {
    if (getDefaultTnCResponse?.data) {
      setDefaultTnC(getDefaultTnCResponse.data);
      let defaultTnC;
      getDefaultTnCResponse.data?.forEach(element => {
        const tnc = { [element.id]: element.text };
        defaultTnC = { ...defaultTnC, ...tnc };
      });
      form.setFieldsValue(defaultTnC);
    }
  }, [getDefaultTnCResponse]);

  useEffect(() => {
    if (getProductAccessoriesResponse?.data) {
      setProductAccessories(getProductAccessoriesResponse.data);
    }
  }, [getProductAccessoriesResponse]);

  useEffect(() => {
    if (getProductSpecificationResponse?.data && !getProductSpecificationResponse?.isLoading) {
      form.setFieldValue(
        ["product_details", productSpecificationUniqueFieldName, "specifications"],
        getProductSpecificationResponse.data
      );
    }
  }, [getProductSpecificationResponse]);

  useEffect(() => {
    if (getProductsResponse?.data) {
      setProducts(getProductsResponse.data);
    }
  }, [getProductsResponse]);

  useEffect(() => {
    if (response.data) {
      navigate("/portal/quotation");
      message.success("Quotation added successfully.");
    }
  }, [response]);

  useEffect(() => {
    if (editResponse.data) {
      navigate("/portal/quotation");
      message.success("Quotation updated successfully.");
    }
  }, [editResponse]);

  useEffect(() => {
    if (getQuotationResponse.data) {
      const { data } = getQuotationResponse;
      const selectedRecord = data[0];
      selectedRecord.shipment_date = formatDateToDayJS(
        selectedRecord.shipment_date
      );
      let defaultTnC;
      selectedRecord.terms_conditions?.forEach(element => {
        const tnc = { [element.id]: element.text };
        defaultTnC = { ...defaultTnC, ...tnc };
      });
      selectedRecord.product_details?.forEach((element) =>
        getAccessories(element.product_id)
      );
      form.setFieldsValue(defaultTnC);
      form.setFieldsValue(selectedRecord);
      setSelectedRecord(selectedRecord);
    }
  }, [getQuotationResponse]);

  // useEffect(() => {
  //   if (mailQuotationResponse.data) {
  //     navigate("/portal/quotation");
  //     message.success("Mail sent successfully.");
  //   }
  // }, [mailQuotationResponse]);

  return (
    <>
      <Card
        title={selectedRecord ? "Update Quotation" : "New Quotation"}
        extra={
          <>
            {selectedRecord && (
              <Space>
                <Button type="primary" onClick={() => setShowEmailModal(true)}>
                  <MailOutlined /> Email Qoutation
                </Button>

                <Button type="primary" onClick={() => printQuotation()}>
                  <PrinterOutlined /> Print Quotation
                </Button>
              </Space>
            )}
          </>
        }
      >
        <Form
          layout="vertical"
          form={form}
          disabled={location.state && location.state.isViewOnly}
        >
          <Row gutter={16}>
            <Divider>
              <b>Customer & Quotation Details</b>
            </Divider>
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item name="quotation_number" label="Quotation Number">
                <Input disabled={true} />
              </Form.Item>
            </Col>
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item
                name="customer_id"
                label="Customer"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Select
                  placeholder="Select Customer"
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {clients?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.first_name + " " + option.last_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {selectedRecord?.organisation && <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item label="Organization">
                <Input value={selectedRecord.organisation} disabled />
              </Form.Item>
            </Col>}
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item name="payment_mode_id" label="Payment Mode">
                <Select
                  placeholder="Select Payment Mode"
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {paymentModes?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.mode}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item name="shipment_date" label="Shipment Date">
                <DatePicker
                  placeholder="Select Date"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item name="shipment_mode_id" label="Shipment Mode">
                <Select
                  placeholder="Select Shipment Mode"
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {shipmentModes?.map((option) => (
                    <Select.Option key={option.id} value={option.id}>
                      {option.mode}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={8} md={24} sm={24} xs={24}>
              <Form.Item name="warranty" label="Warranty (in year)">
                <Select
                  placeholder="Select Warranty"
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {[1, 2, 3, 4, 5]?.map((option) => (
                    <Select.Option key={option} value={option}>
                      {option + " year(s)"}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="validity" label="Quotation Validity (in Days)">
                <Select
                  placeholder="Select Validity"
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {[60, 120, 180].map((option) => (
                    <Select.Option key={option} value={option}>
                      {option + " days"}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Divider>
              <b>Product Details</b>
            </Divider>
            <Form.List name="product_details">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <>
                      <Space
                        key={key}
                        style={{
                          display: "flex",
                          marginBottom: 8,
                        }}
                        align="baseline"
                      >
                        <Row>
                          <Form.Item
                            {...restField}
                            label="Product Name"
                            name={[name, "product_id"]}
                            rules={[
                              {
                                required: true,
                                message: "Please select product.",
                              },
                            ]}
                          >
                            <Select
                              placeholder="Select Product"
                              style={{ minWidth: 350 }}
                              showSearch
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              onChange={(productId) => onProductSelect(productId, name)}
                            >
                              {products?.map((option) => (
                                <Select.Option
                                  key={option.id}
                                  value={option.id}
                                >
                                  {option.product_name}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>

                        </Row>
                        <Row>
                          <Form.Item
                            {...restField}
                            label="Quantity"
                            name={[name, "quantity"]}
                            rules={[
                              {
                                required: true,
                                message: "Please enter quantity.",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ minWidth: 200 }}
                              placeholder="Quantity"
                              onChange={() =>
                                calculateProductDetailsTotal(name)
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Price"
                            name={[name, "price"]}
                            rules={[
                              {
                                required: true,
                                message: "Please enter price.",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ minWidth: 200 }}
                              placeholder="Price"
                              onChange={() =>
                                calculateProductDetailsTotal(name)
                              }
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...restField}
                            label="Discount (%)"
                            name={[name, "discount_percent"]}
                          >
                            <InputNumber
                              style={{ minWidth: 200 }}
                              placeholder="Discount Percent"
                              onChange={() =>
                                calculateProductDetailsTotal(name)
                              }
                            />
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            label="Discount ($)"
                            name={[name, "discount_amount"]}
                          >
                            <InputNumber
                              style={{ minWidth: 200 }}
                              placeholder="Discount Amount"
                              onChange={() =>
                                calculateProductDetailsTotal(name)
                              }
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...restField}
                            label="Amount ($)"
                            name={[name, "amount"]}
                            rules={[
                              {
                                required: true,
                                message: "Please enter amount.",
                              },
                            ]}
                          >
                            <InputNumber
                              style={{ minWidth: 200 }}
                              placeholder="Amount"
                            />
                          </Form.Item>
                          <Button
                            type="primary"
                            style={{ marginBottom: 20, marginRight: 20 }}
                            onClick={() => {
                              remove(name);
                              calculateProductDetailsTotal();
                            }}
                          >
                            <MinusCircleOutlined /> Product
                          </Button>
                        </Row>
                      </Space>
                      <Row style={{ width: "100%", paddingBottom: 40 }}>
                        <Col span={24}>
                          <Collapse
                            items={[{
                              key: '1', label: 'Product Specifications', children:
                                <Form.List name={[name, "specifications"]}>
                                  {(subFields, subOpt) => (
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        rowGap: 8
                                      }}
                                    >
                                      {subFields.map((subField) => (
                                        <Space key={subField.key}>
                                          <Form.Item
                                            noStyle
                                            name={[subField.name, "parameter"]}
                                          >
                                            <Input style={{ minWidth: 300 }} />
                                          </Form.Item>
                                          <Form.Item
                                            noStyle
                                            name={[subField.name, "specifications"]}
                                          >
                                            <Input style={{ minWidth: 500 }} />
                                          </Form.Item>
                                          <CloseOutlined
                                            onClick={() => {
                                              subOpt.remove(subField.name);
                                            }}
                                          />
                                        </Space>
                                      ))}
                                      <Button
                                        type="dashed"
                                        onClick={() => subOpt.add()}
                                        block
                                        icon={"+"}
                                      >
                                        Specification
                                      </Button>
                                    </div>
                                  )}
                                </Form.List>
                            }]}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col span={24}>
                          {/* Nest Form.List */}
                          <Form.Item label={<b>Choose Accessories</b>}>
                            <Form.List name={[name, "product_accessories"]}>
                              {(subFields, subOpt) => (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    rowGap: 16,
                                  }}
                                >
                                  {subFields.map((subField) => (
                                    <Space key={subField.key}>
                                      <Form.Item
                                        noStyle
                                        name={[subField.name, "accessory_id"]}
                                      >
                                        <Select
                                          placeholder="Select Accessories"
                                          style={{ minWidth: 350 }}
                                          showSearch
                                          filterOption={(input, option) =>
                                            option.children
                                              .toLowerCase()
                                              .indexOf(input.toLowerCase()) >= 0
                                          }
                                        >
                                          {productAccessories?.map((option) => (
                                            <Select.Option
                                              key={option.id}
                                              value={option.id}
                                            >
                                              {option.accessory}
                                            </Select.Option>
                                          ))}
                                        </Select>
                                      </Form.Item>
                                      <Form.Item
                                        noStyle
                                        name={[subField.name, "quantity"]}
                                      >
                                        <InputNumber
                                          style={{ minWidth: 200 }}
                                          placeholder="Quantity"
                                          onBlur={() =>
                                            calculateProductAccessoriesTotal(
                                              name,
                                              subField.name
                                            )
                                          }
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        noStyle
                                        name={[subField.name, "price"]}
                                      >
                                        <InputNumber
                                          style={{ minWidth: 200 }}
                                          placeholder="Price"
                                          onBlur={() =>
                                            calculateProductAccessoriesTotal(
                                              name,
                                              subField.name
                                            )
                                          }
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        noStyle
                                        name={[subField.name, "amount"]}
                                      >
                                        <InputNumber
                                          style={{ minWidth: 200 }}
                                          placeholder="Amount"
                                          onBlur={() =>
                                            calculateProductAccessoriesTotal(
                                              name,
                                              subField.name
                                            )
                                          }
                                        />
                                      </Form.Item>
                                      <CloseOutlined
                                        onClick={() => {
                                          subOpt.remove(subField.name);
                                        }}
                                      />
                                    </Space>
                                  ))}
                                  <Button
                                    type="dashed"
                                    onClick={() => subOpt.add()}
                                    block
                                    icon={"+"}
                                  >
                                    Accessories
                                  </Button>
                                </div>
                              )}
                            </Form.List>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ))}
                  <Button
                    type="primary"
                    onClick={() => add()}
                    block={true}
                    icon={"+"}
                  >
                    Product
                  </Button>
                </>
              )}
            </Form.List>
          </Row>
          <Row gutter={16}>
            <Divider>
              <b>Total Estimate</b>
            </Divider>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item name="total_item_amount" label="Items Total">
                <InputNumber placeholder="Items Total" disabled />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Discount (%)" name="total_discount_percent">
                <InputNumber
                  placeholder="(in %)"
                  onChange={calculateTotalAmount}
                />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Discount ($)" name="total_discount_amount">
                <InputNumber
                  placeholder="(in $)"
                  onChange={calculateTotalAmount}
                />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Shipping & Handling Charges" name="shipping">
                <InputNumber
                  placeholder="Shipping & Handling Charges"
                  onChange={calculateTotalAmount}
                />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Bank Charges" name="bank_charges">
                <InputNumber
                  placeholder="Bank Charges"
                  onChange={calculateTotalAmount}
                />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Adjustment" name="adjustment">
                <InputNumber
                  placeholder="Adjustment"
                  onChange={calculateTotalAmount}
                />
              </Form.Item>
            </Col>
            <Col lg={6} md={24} sm={24} xs={24}>
              <Form.Item label="Total" name="total">
                <InputNumber placeholder="Total" disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Divider>
              <b>Standard Terms & Conditions</b>
            </Divider>
            <Space direction="vertical" style={{ width: "100%" }}>
              {!isEmpty(selectedRecord)
                ? selectedRecord.terms_conditions?.map((condition, index) => (
                  <Row gutter={16}>
                    <Col span={22}>
                      <Form.Item key={condition.id} name={condition.id}>
                        <TextArea />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <DeleteOutlined style={{ fontSize: 20 }} onClick={() => deleteCondition(condition.id)} />
                    </Col>
                  </Row>
                ))
                : defaultTnC?.map((condition, index) => (
                  <Row gutter={16}>
                    <Col span={22}>
                      <Form.Item key={condition.id} name={condition.id}>
                        <TextArea />
                      </Form.Item>
                    </Col>
                    <Col span={2}>
                      <DeleteOutlined style={{ fontSize: 20 }} onClick={() => deleteCondition(condition.id)} />
                    </Col>
                  </Row>
                ))}
            </Space>
          </Row>
          <Row gutter={16}>
            {/* <Divider><b>Other Condition</b></Divider> */}
            <Form.List name="other_conditions">
              {(fields, { add, remove }) => (
                <Col span={24}>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16}>
                      <Col span={22}>
                        <Form.Item
                          {...restField}
                          name={[name, "text"]}
                          rules={[
                            {
                              required: true,
                              message: "Please enter text.",
                            },
                          ]}
                        >
                          <TextArea placeholder="Condition" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <DeleteOutlined style={{ fontSize: 20 }} onClick={() => remove(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button
                      type="primary"
                      onClick={() => add()}
                      block
                      icon={"+"}
                    >
                      Add Condition
                    </Button>
                  </Form.Item>
                </Col>
              )}
            </Form.List>
          </Row>
          <Row style={{ float: "right" }}>
            <Col span={24}>
              <Button
                type="primary"
                style={{ marginRight: 10 }}
                onClick={handleOk}
              >
                {location.state ? "Update" : "Save"}
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {selectedRecord && (
        <div style={{ visibility: "hidden", display: "none" }}>
          <QuotationPrint
            componentRef={componentRef}
            quotationDetails={selectedRecord}
          />
        </div>
      )}

      {showEmailModal && (
        <EmailModal
          options={{
            to: [selectedRecord.customer_primary_mail],
            subject: `Quotation ${selectedRecord.quotation_number}`,
          }}
          defaultBodyFormat={QuotationEmailFormat(selectedRecord)}
          open={showEmailModal}
          close={() => setShowEmailModal(false)}
          onOk={sendQuotationEmail}
        />
      )}
    </>
  );
}

export default AddQuotation;
